import { CSSObject } from "@emotion/react";
import styled from "@emotion/styled";
import { Flex } from "@otrium/core";
import {
  getButtonStylesByVariant,
  LeftIcon,
} from "src/atoms/Button/Button.styled";
import { Button } from "src/atoms/Button";
import { CreateAccountBlock } from "src/molecules/CreateAccountBlock";
import {
  contentIn,
  ENTER_EASE,
  fadeIn,
  iconIn,
  motionSafe,
  strokeDraw,
} from "src/motion";

export const ContinueShopping = styled.div`
  display: none;

  @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
    display: flex;
    margin-block-start: ${({ theme }) => theme.space.space24};
  }
`;

export const CreateAccountCard = styled(CreateAccountBlock)`
  margin-block-start: ${({ theme }) => theme.space.space16};

  @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
    margin-block-start: ${({ theme }) => theme.space.space24};
  }
`;

export const CreateAccountForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.space.space16};
`;

export const CreateAccountButton = styled(Button)`
  align-self: flex-start;
  margin-block-start: ${({ theme }) => theme.space.space8};
`;

export const PasswordToggle = styled.button`
  display: inline-flex;
  flex-shrink: 0;
  align-items: center;
  align-self: stretch;
  padding-inline: 11px;
  border: none;
  background: none;
  cursor: pointer;
`;

export const ConfirmationCard = styled.div`
  padding: ${({ theme }) => theme.space.space24}
    ${({ theme }) => theme.space.space16};
  background-color: ${({ theme }) => theme.colors.tone.sand};

  @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
    padding: ${({ theme }) => theme.space.space32}
      ${({ theme }) => theme.space.space24};
    border-radius: ${({ theme }) => theme.effects.rounding._8};
  }
`;

export const AppDownloadCard = styled.div`
  padding: ${({ theme }) => theme.space.space32}
    ${({ theme }) => theme.space.space24};
  border-radius: ${({ theme }) => theme.effects.rounding._8};
  background-color: ${({ theme }) => theme.colors.primary.goldLight};

  @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
    padding: calc(${({ theme }) => theme.space.space32} - 1px)
      calc(${({ theme }) => theme.space.space24} - 1px);
    border: ${({ theme }) => theme.effects.line.border};
  }
`;

export const ReferAFriendCard = styled.div`
  border: ${({ theme }) => theme.effects.line.border};
  border-radius: ${({ theme }) => theme.effects.rounding._8};
  overflow: hidden;
`;

export const ReferAFriendImage = styled.div`
  position: relative;
  aspect-ratio: 3 / 2;
  overflow: hidden;

  img {
    display: block;
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

export const ReferAFriendBody = styled.div`
  padding: calc(${({ theme }) => theme.space.space24} - 1px);
`;

export const FeedbackGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: ${({ theme }) => theme.space.space8};

  @media (min-width: ${({ theme }) => theme.breakpoints.lg}) {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
`;

export const FeedbackRows = styled.div`
  display: grid;
  gap: ${({ theme }) => theme.space.space8};

  @media (min-width: ${({ theme }) => theme.breakpoints.lg}) {
    grid-template-columns: repeat(2, minmax(0, 1fr));

    > :last-child:nth-child(odd) {
      grid-column: 1 / -1;
    }
  }
`;

export const FollowUp = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.space.space8};
  margin-block-start: ${({ theme }) => theme.space.space16};
`;

export const FollowUpLabel = styled.label`
  font-size: ${({ theme }) => theme.fontSizes.fontSize14};
  line-height: 20px;
`;

export const FollowUpTextArea = styled.textarea`
  width: 100%;
  min-height: 52px;
  padding: 12px ${({ theme }) => theme.space.space16};
  border: ${({ theme }) => theme.effects.line.border};
  border-radius: ${({ theme }) => theme.effects.rounding._4};
  background-color: ${({ theme }) => theme.colors.tone.white};
  color: ${({ theme }) => theme.colors.tone.black};
  line-height: 24px;
  resize: vertical;

  &::placeholder {
    color: ${({ theme }) => theme.colors.tone.anthracite};
  }

  &:focus-visible {
    border-color: ${({ theme }) => theme.colors.semantic.blue};
    outline: none;
  }
`;

export const FollowUpActions = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.space.space16};
  margin-block-start: ${({ theme }) => theme.space.space16};
`;

export const SmallButton = styled(Button)`
  height: 40px;
  padding-inline: ${({ theme }) => theme.space.space24};
`;

export const SkipButton = styled.button`
  display: inline-flex;
  align-items: center;
  height: 40px;
  padding: 0;
  border: none;
  background: none;
  color: ${({ theme }) => theme.colors.tone.black};
  font-size: ${({ theme }) => theme.fontSizes.fontSize16};
  font-weight: 600;
  line-height: 24px;
  text-decoration: underline;
  text-decoration-thickness: 1px;
  text-underline-offset: 2px;
  cursor: pointer;
`;

export const AppDownloadRow = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.space.space16};
  margin-block-start: ${({ theme }) => theme.space.space24};
`;

export const AppDownloadQr = styled.div`
  display: none;
  flex-shrink: 0;
  width: 112px;
  height: 112px;
  background-image: url("/static/images/app-download-qr.svg");
  background-repeat: no-repeat;
  background-size: contain;

  @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
    display: block;
  }
`;

export const AppDownloadButtons = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: ${({ theme }) => theme.space.space16};
  min-height: 48px;
`;

export const UnderlinedLink = styled.a`
  color: inherit;
  text-decoration: underline;

  &:hover {
    text-decoration: none;
  }
`;

export const SubscribeFormWrapper = styled.form`
  margin-block-start: ${({ theme }) => theme.space.space16};

  @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
    margin-block-start: ${({ theme }) => theme.space.space24};
  }
`;

export const SubscribeButton = styled(Button)`
  @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
    ${({ theme }) =>
      getButtonStylesByVariant(theme, "primary", "dark", true) as CSSObject}

    &:active {
      background-color: ${({ theme }) => theme.colors.tone.anthraciteDark};
    }

    &:disabled {
      border: none;
    }
  }
`;

export const SubscribeDisclaimer = styled.p`
  margin: ${({ theme }) => theme.space.space16} 0 0;
  font-size: ${({ theme }) => theme.fontSizes.fontSize12};
  line-height: 16px;
`;

export const StoreButton = styled.div`
  ${LeftIcon} > svg {
    width: auto;
    height: 18.33px;
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
    position: relative;

    ${LeftIcon} {
      position: absolute;
      left: ${({ theme }) => theme.space.space16};
      top: 50%;
      transform: translateY(-50%);
      margin-right: 0;

      > svg {
        height: 22px;
      }
    }
  }
`;

export const SemiboldEmail = styled.span`
  font-weight: 600;
`;

export const ReferAFriendLink = styled.p`
  margin: ${({ theme }) => theme.space.space24} 0 0;
  font-size: ${({ theme }) => theme.fontSizes.fontSize14};
  line-height: 20px;
`;

export const NoticeRow = styled(Flex)`
  ${motionSafe`${contentIn} 420ms ${ENTER_EASE} both,
        ${fadeIn} 320ms ease-out both`};

  svg {
    ${motionSafe`${iconIn} 520ms 60ms ${ENTER_EASE} both`};
  }

  [data-draw] {
    stroke-dasharray: 1;
  }

  [data-draw="ring"] {
    ${motionSafe`${strokeDraw} 520ms 80ms ${ENTER_EASE} both`};
  }

  [data-draw="tick"] {
    ${motionSafe`${strokeDraw} 340ms 320ms ${ENTER_EASE} both`};
  }
`;
